import React, { useEffect, useState, memo, useCallback, useRef } from "react";
import noProfile from "../img/noprofile.jpg";
import Box from '@mui/material/Box';

// Cache global avec nettoyage automatique
const imageCache = new Map();
const MAX_CACHE_SIZE = 100;

// Fonction pour nettoyer le cache si trop grand
const cleanCache = () => {
  if (imageCache.size > MAX_CACHE_SIZE) {
    const keysToDelete = Array.from(imageCache.keys()).slice(0, 20);
    keysToDelete.forEach(key => imageCache.delete(key));
  }
};

// Fonction pour invalider une entrée spécifique du cache
export const invalidateImageCache = (id, imgUrl, ext) => {
  const baseKey = `${imgUrl}${id}.${ext}`;
  // Supprimer toutes les versions avec timestamp
  for (const key of imageCache.keys()) {
    if (key.startsWith(baseKey)) {
      imageCache.delete(key);
    }
  }
};

const Avatar = memo(({
  id,
  imgUrl,
  ext,
  onClick,
  update = 0,
  width = "40px",
  height = "40px",
  style,
  editable = true,
  badgeType = null,
  showBadge = false,
}) => {
  // Utiliser un timestamp uniquement si update change
  const imageUrl = update > 0
    ? `${imgUrl}${id}.${ext}?v=${update}`
    : `${imgUrl}${id}.${ext}`;

  const cacheKey = imageUrl;

  const [imageSrc, setImageSrc] = useState(() => {
    const cached = imageCache.get(cacheKey);
    return cached === undefined ? null : cached;
  });

  const [imageError, setImageError] = useState(() => {
    const cached = imageCache.get(cacheKey);
    return cached === null;
  });

  const [isLoading, setIsLoading] = useState(() => {
    return imageCache.get(cacheKey) === undefined;
  });

  const isMountedRef = useRef(true);
  const currentLoadRef = useRef(null);

  useEffect(() => {
    isMountedRef.current = true;

    // Si en cache, utiliser directement
    const cached = imageCache.get(cacheKey);
    if (cached !== undefined) {
      if (isMountedRef.current) {
        setImageSrc(cached);
        setImageError(cached === null);
        setIsLoading(false);
      }
      return;
    }

    // Annuler le chargement précédent si existe
    if (currentLoadRef.current) {
      currentLoadRef.current.cancelled = true;
    }

    const loadContext = { cancelled: false };
    currentLoadRef.current = loadContext;

    setIsLoading(true);

    // Charger l'image
    const img = new Image();

    img.onload = () => {
      if (isMountedRef.current && !loadContext.cancelled) {
        imageCache.set(cacheKey, imageUrl);
        cleanCache();
        setImageSrc(imageUrl);
        setImageError(false);
        setIsLoading(false);
      }
    };

    img.onerror = () => {
      if (isMountedRef.current && !loadContext.cancelled) {
        imageCache.set(cacheKey, null);
        cleanCache();
        setImageSrc(null);
        setImageError(true);
        setIsLoading(false);
      }
    };

    img.src = imageUrl;

    return () => {
      isMountedRef.current = false;
      if (currentLoadRef.current) {
        currentLoadRef.current.cancelled = true;
      }
    };
  }, [cacheKey, imageUrl]);

  const handleImageError = useCallback(() => {
    setImageError(true);
    setImageSrc(null);
    setIsLoading(false);
  }, []);

  const handleClick = useCallback((e) => {
    if (onClick) {
      e.stopPropagation();
      onClick(e);
    }
  }, [onClick]);

  const badgeConfig = {
    'A': { label: 'A', color: '#0075E3', bgColor: '#0075E3' },
    'E': { label: 'E', color: '#EF8C23', bgColor: '#EF8C23' }
  };
  const badgeStyle = showBadge && badgeType && badgeConfig[badgeType]
    ? {
      position: 'absolute',
      bottom: '0',
      right: '-4px', //-4 //-8
      background: badgeConfig[badgeType].bgColor,
      color: 'white',
      borderRadius: '50%',
      width: '18px',
      height: '18px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '9px',
      fontWeight: 'bold',
      border: '2px solid white',
      boxShadow: `0 2px 6px ${badgeConfig[badgeType].bgColor}40`,
          //box-shadow: 0 2px 6px #0075E340;
      zIndex: 1,
      flexShrink: 0
    }
    : null;


  return (
    <Box
      className="userprofile-pic"
      onClick={handleClick}
      style={{
        width,
        height,
        cursor: onClick ? 'pointer' : 'default',
        position: 'relative',
        overflow: 'visible',
        ...style
      }}
    >
      {isLoading && !imageSrc && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: '#f3f4f6',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <span style={{ fontSize: '10px', color: '#9ca3af' }}>...</span>
        </div>
      )}
      <img
        src={imageError || !imageSrc ? noProfile : imageSrc}
        alt=""
        className={editable ? 'avatar' : 'avatar-non-editable'}
        id="avatar"
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          borderRadius: '50%',
          display: isLoading && !imageSrc ? 'none' : 'block'
        }}
        onError={handleImageError}
      />
      {badgeStyle && (
        <div style={badgeStyle} title={badgeType === 'A' ? 'Associé' : 'Employé'}>
          {badgeConfig[badgeType].label}
        </div>
      )}
    </Box>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.id === nextProps.id &&
    prevProps.imgUrl === nextProps.imgUrl &&
    prevProps.ext === nextProps.ext &&
    prevProps.update === nextProps.update &&
    prevProps.badgeType === nextProps.badgeType &&
    prevProps.showBadge === nextProps.showBadge
  );
});

Avatar.displayName = 'Avatar';

export default Avatar;